/**
 * ChangeUnionTypeAccountsStep — wizard-шаг «Счета СФУ».
 *
 * Используется в ветке CHANGE_UNION_TYPE (смена формы управления).
 * Заменяет стандартный шаг RegistrationAccounts.
 *
 * При маунте загружает список групп услуг через:
 *   GET /api/Registrations/{id}/groups-with-current-accounts
 *
 * Для каждой группы рендерит RowEditor (IBAN/BIC/скан или reuse). Тип счёта
 * определяется группой и отображается read-only — селекта нет. Сабмит строки
 * атомарный: POST /api/Registrations/{id}/sfu-accounts создаёт счёт+скан в
 * одной транзакции.
 *
 * Кнопка «Далее» блокируется пока хоть одна строка не заполнена.
 */
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from '@shared/utils/i18n';
import { useGetApiCatalogsBanks } from '@/shared/api/orval/catalogs/catalogs';
import { useRegistrationWizardContext } from '@widgets/registrationWizard/store/RegistrationWizardProvider';
import { WizardButtonsWrapper } from '@widgets/registrationWizard/ui/WizardButtonsWrapper';
import { ChangeUnionTypeAccountsViewModel } from './model/viewModel';
import { RowEditor } from './ui/RowEditor';

export const ChangeUnionTypeAccountsStep: React.FC = observer(() => {
  const { t } = useTranslation();
  const wizard = useRegistrationWizardContext();

  // ViewModel создаётся локально (не через Inversify-контейнер),
  // т.к. шаг монтируется/демонтируется в рамках wizard-провайдера.
  // useMemo гарантирует один экземпляр за время жизни компонента.
  const vm = useMemo(() => new ChangeUnionTypeAccountsViewModel(), []);

  // Каталог банков загружается один раз на уровне шага и пробрасывается во все RowEditor —
  // чтобы автоподстановка BIC по slice(4,7) IBAN не делала повторных запросов на каждую строку.
  const { data: banksResponse } = useGetApiCatalogsBanks();
  const banks = banksResponse?.result ?? [];

  const registrationId = wizard.registration?.id ?? 0;

  useEffect(() => {
    if (!registrationId) return;
    vm.loadGroups(registrationId);
  }, [registrationId, vm]);

  if (vm.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ maxWidth: 1200, minWidth: 800 }}>
        <CardHeader
          title={
            <Typography variant="h5" color="textPrimary">
              {t('registration:wizard.changeUnionType.title')}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('registration:wizard.changeUnionType.subtitle')}
            </Typography>
          }
        />
        <CardContent>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{t('registration:wizard.changeUnionType.columns.group')}</TableCell>
                <TableCell>{t('registration:wizard.changeUnionType.columns.iban')}</TableCell>
                <TableCell>{t('registration:wizard.changeUnionType.columns.bic')}</TableCell>
                <TableCell>{t('registration:wizard.changeUnionType.columns.scan')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vm.rowList.map((row) => (
                <RowEditor key={row.serviceGroupId} row={row} vm={vm} banks={banks} />
              ))}

              {vm.rowList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t('common:empty')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <WizardButtonsWrapper>
        {(wizard.wizardStep ?? 0) !== 0 && (
          <LoadingButton onClick={wizard.prevStep} loading={wizard.isLoading}>
            {t('common:back')}
          </LoadingButton>
        )}
        <Tooltip
          title={
            !vm.isAllRowsComplete
              ? /* Показываем первую незаполненную группу */
                vm.rowList
                  .filter((r) => !r.registrationAccountId || !r.scanDocId)
                  .map((r) =>
                    t('registration:wizard.changeUnionType.errors.rowIncomplete', {
                      group: r.serviceGroupNameRu
                    })
                  )
                  .join('; ')
              : ''
          }
        >
          <span>
            <LoadingButton
              variant="contained"
              disabled={!vm.isAllRowsComplete}
              loading={wizard.isLoading}
              onClick={wizard.nextStep}
            >
              {t('common:next')}
            </LoadingButton>
          </span>
        </Tooltip>
      </WizardButtonsWrapper>
    </Box>
  );
});
